export const BROWN_GTM_CONTAINER_ID = "GTM-M6CBR76X";
export const BROWN_GTM_PATHS = [
  "/sacramento-cash-offer",
  "/sacramento-cash-offer/contact-step-2",
  "/sacramento-cash-offer/contact",
] as const;

export function isBrownGtmPath(pathname: string): boolean {
  const normalized = pathname.replace(/\/+$/, "");
  return BROWN_GTM_PATHS.some(path => path === normalized);
}

export const BROWN_GOOGLE_ADS_TAG_ID = "AW-18529679427";
export const BROWN_GOOGLE_ADS_PHONE_CONVERSION_ACTION =
  "AW-18529679427/vG8KCKC1ga8dEMPw0YNF";
const BROWN_GOOGLE_ADS_PHONE_NUMBER = "(916) 545-7205";
const BROWN_GOOGLE_ADS_PHONE_SCRIPT_ID = "brown-google-ads-phone-conversion";
const BROWN_GOOGLE_ADS_TAG_SCRIPT_ID = "brown-google-ads-gtag-js";
const BROWN_GOOGLE_ADS_CONFIG_SCRIPT_ID = "brown-google-ads-config";

const googleAdsConfigScript = `window.dataLayer = window.dataLayer || [];
function gtag(){dataLayer.push(arguments);}
gtag('js', new Date());

gtag('config', '${BROWN_GOOGLE_ADS_TAG_ID}');`;

const googleAdsHeadSnippet = `<!-- Google tag (gtag.js) -->
<script async id="${BROWN_GOOGLE_ADS_TAG_SCRIPT_ID}" src="https://www.googletagmanager.com/gtag/js?id=${BROWN_GOOGLE_ADS_TAG_ID}"></script>
<script id="${BROWN_GOOGLE_ADS_CONFIG_SCRIPT_ID}">
  ${googleAdsConfigScript}
</script>`;

declare global {
  interface Window {
    brownGoogleAdsPhoneConversionPath?: string | null;
  }
}

/** Add the supplied Google Ads phone-conversion config to brown-page body content. */
export function configureBrownGoogleAdsPhoneConversion(pathname: string): void {
  if (!isBrownGtmPath(pathname)) {
    window.brownGoogleAdsPhoneConversionPath = null;
    return;
  }

  const normalizedPath = pathname.replace(/\/+$/, "");
  if (window.brownGoogleAdsPhoneConversionPath === normalizedPath) return;

  const existingScript = document.getElementById(BROWN_GOOGLE_ADS_PHONE_SCRIPT_ID);
  if (existingScript) {
    window.gtag?.("config", BROWN_GOOGLE_ADS_PHONE_CONVERSION_ACTION, {
      phone_conversion_number: BROWN_GOOGLE_ADS_PHONE_NUMBER,
    });
  } else {
    const script = document.createElement("script");
    script.id = BROWN_GOOGLE_ADS_PHONE_SCRIPT_ID;
    script.textContent = `gtag('config', '${BROWN_GOOGLE_ADS_PHONE_CONVERSION_ACTION}', {
    'phone_conversion_number': '${BROWN_GOOGLE_ADS_PHONE_NUMBER}'
  });`;
    document.body.appendChild(script);
  }

  window.brownGoogleAdsPhoneConversionPath = normalizedPath;
}

/** Load the brown Google Ads destination on direct loads and SPA route entry. */
export function ensureBrownGoogleAdsTagLoaded(pathname: string): void {
  if (!isBrownGtmPath(pathname)) return;

  if (document.getElementById(BROWN_GOOGLE_ADS_TAG_SCRIPT_ID)) return;

  const tagScript = document.createElement("script");
  tagScript.async = true;
  tagScript.id = BROWN_GOOGLE_ADS_TAG_SCRIPT_ID;
  tagScript.src = `https://www.googletagmanager.com/gtag/js?id=${BROWN_GOOGLE_ADS_TAG_ID}`;
  document.head.appendChild(tagScript);

  const configScript = document.createElement("script");
  configScript.id = BROWN_GOOGLE_ADS_CONFIG_SCRIPT_ID;
  configScript.textContent = googleAdsConfigScript;
  document.head.appendChild(configScript);
}

const headSnippet = `<!-- Google Tag Manager -->
<script id="brown-gtm-bootstrap">(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
})(window,document,'script','dataLayer','${BROWN_GTM_CONTAINER_ID}');</script>
<!-- End Google Tag Manager -->`;

const bodySnippet = `<!-- Google Tag Manager (noscript) -->
<noscript id="brown-gtm-noscript"><iframe src="https://www.googletagmanager.com/ns.html?id=${BROWN_GTM_CONTAINER_ID}"
height="0" width="0" style="display:none;visibility:hidden"></iframe></noscript>
<!-- End Google Tag Manager (noscript) -->`;

/** Used for development HTML and production brown routes, including legacy links. */
export function injectBrownGtmHtml(html: string): string {
  let result = html;
  if (!result.includes('id="brown-gtm-bootstrap"')) {
    result = result.replace("<head>", `<head>\n    ${headSnippet}`);
  }
  if (!result.includes(`id="${BROWN_GOOGLE_ADS_TAG_SCRIPT_ID}"`)) {
    result = result.replace("</head>", `${googleAdsHeadSnippet}\n</head>`);
  }
  if (!result.includes('id="brown-gtm-noscript"')) {
    // Development plugins contain "<body>" inside their inline scripts.
    // Consume script blocks intact so the fallback is inserted in the real body.
    result = result.replace(
      /<script\b[^>]*>[\s\S]*?<\/script>|<body>/gi,
      tag => tag.toLowerCase() === "<body>" ? `${tag}\n    ${bodySnippet}` : tag,
    );
  }
  return result;
}

declare global {
  interface Window {
    dataLayer?: unknown[];
  }
}

/** Also cover client-side entry from another audience, without reloading GTM. */
export function ensureBrownGtmLoaded(): void {
  if (document.querySelector(`script[src*="/gtm.js?id=${BROWN_GTM_CONTAINER_ID}"]`)) return;
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ "gtm.start": Date.now(), event: "gtm.js" });
  const script = document.createElement("script");
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtm.js?id=${BROWN_GTM_CONTAINER_ID}`;
  document.head.appendChild(script);
}
